import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'motion/react';
import { RiGithubFill, RiGoogleFill, RiEyeLine, RiEyeOffLine, RiDatabase2Line } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { authClient } from '@/lib/auth-client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lastSignInMethod, setLastSignInMethod] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Check for last sign-in method on mount
  useEffect(() => {
    const lastMethod = localStorage.getItem('lastSignInMethod');
    setLastSignInMethod(lastMethod);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        const errorMessage = result.error.message || 'Failed to sign in';

        // Check if error is about email not being verified
        if (errorMessage.toLowerCase().includes('email not verified') ||
            errorMessage.toLowerCase().includes('not verified')) {
          toast.error('Email not verified. Redirecting to verification page...');
          // Redirect to verify email page with the email
          setTimeout(() => {
            navigate({ to: '/verify-email', search: { email: data.email } });
          }, 1500);
          return;
        }

        toast.error(errorMessage);
        return;
      }

      // Wait for session to be established before redirecting
      await authClient.session.get();

      // Store sign-in method for next time
      localStorage.setItem('lastSignInMethod', 'email');

      toast.success('Signed in successfully');
      // Use window.location for hard navigation to ensure session is loaded
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('An unexpected error occurred');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      // Store sign-in method before redirect
      localStorage.setItem('lastSignInMethod', 'google');
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (error) {
      setGoogleLoading(false);
      toast.error('Failed to sign in with Google');
      console.error('Google sign in error:', error);
    }
  };

  const handleGithubSignIn = async () => {
    setGithubLoading(true);
    try {
      // Store sign-in method before redirect
      localStorage.setItem('lastSignInMethod', 'github');
      await authClient.signIn.social({
        provider: 'github',
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (error) {
      setGithubLoading(false);
      toast.error('Failed to sign in with GitHub');
      console.error('GitHub sign in error:', error);
    }
  };
  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center flex-1 p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full max-w-md space-y-8"
        >
          {/* Logo */}
          <div className="flex items-center gap-3">
            {/* <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10"> */}
              {/* <RiDatabase2Line className="size-6 text-primary" /> */}
              <img src={logoSrc} alt="Moniva" className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">Moniva</h1>
              <p className="text-sm text-muted-foreground">Database Management</p>
            </div>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-muted-foreground">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              {lastSignInMethod === 'google' && (
                <Badge
                  variant="secondary"
                  className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/20 z-10"
                >
                  Last used
                </Badge>
              )}
              <Button
                variant={lastSignInMethod === 'google' ? 'default' : 'outline'}
                onClick={handleGoogleSignIn}
                disabled={isLoading || googleLoading || githubLoading}
                className="w-full"
              >
                {googleLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 rounded-full border-current border-t-transparent"
                  />
                ) : (
                  <RiGoogleFill className="size-5" />
                )}
              </Button>
            </div>

            <div className="relative">
              {lastSignInMethod === 'github' && (
                <Badge
                  variant="secondary"
                  className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/20 z-10"
                >
                  Last used
                </Badge>
              )}
              <Button
                variant={lastSignInMethod === 'github' ? 'default' : 'outline'}
                onClick={handleGithubSignIn}
                disabled={isLoading || googleLoading || githubLoading}
                className="w-full"
              >
                {githubLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 rounded-full border-current border-t-transparent"
                  />
                ) : (
                  <RiGithubFill className="size-5" />
                )}
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-background text-muted-foreground inline-flex items-center gap-2">
                Or continue with email
                {lastSignInMethod === 'email' && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary border-primary/20 normal-case"
                  >
                    Last used
                  </Badge>
                )}
              </span>
            </div>
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Password</FormLabel>
                      <button
                        type="button"
                        onClick={() => navigate({ to: '/forgot-password' })}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          disabled={isLoading}
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute transition-colors -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <RiEyeOffLine className="size-4" />
                          ) : (
                            <RiEyeLine className="size-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 rounded-full border-primary-foreground border-t-transparent"
                  />
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </Form>

          {/* Footer */}
          <p className="text-sm text-center text-muted-foreground">
            Don't have an account?{' '}
            <button
              onClick={() => navigate({ to: '/signup' })}
              className="font-medium text-primary hover:underline"
            >
              Sign up
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image/Illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        className="items-center justify-center flex-1 hidden p-12 lg:flex bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="max-w-lg space-y-6 text-center relative z-10">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            src="/nodata.svg"
            alt="Database Management"
            className="w-full h-auto opacity-90"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="space-y-3"
          >
            <h3 className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Manage Your Databases Visually
            </h3>
            <p className="text-base text-muted-foreground leading-relaxed">
              Connect, query, and manage your PostgreSQL, MySQL, and SQLite databases all in one place.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
